/* Vue 3 ports of the simple (formerly functional) CoreUI v3 components. Markup/classes unchanged. */
import { h } from 'vue'
import { CLink, propsFactory as linkPropsFactory, props as linkProps } from './link'
import { pickByKeys } from './utils'

const slot = (slots) => (slots.default ? slots.default() : undefined)

export const CContainer = {
  name: 'CContainer',
  props: { fluid: Boolean, tag: { type: String, default: 'div' } },
  render () {
    return h(this.tag, { class: { container: !this.fluid, 'container-fluid': this.fluid } }, slot(this.$slots))
  }
}

export const CRow = {
  name: 'CRow',
  props: {
    tag: { type: String, default: 'div' },
    gutters: { type: Boolean, default: true },
    alignVertical: String,
    alignHorizontal: String,
    form: Boolean
  },
  render () {
    return h(this.tag, {
      class: [this.form ? 'form-row' : 'row', {
        'no-gutters': !this.gutters,
        [`align-items-${this.alignVertical}`]: this.alignVertical,
        [`justify-content-${this.alignHorizontal}`]: this.alignHorizontal
      }]
    }, slot(this.$slots))
  }
}

const colProp = [Boolean, String, Number, Object]
export const CCol = {
  name: 'CCol',
  props: { col: colProp, xs: colProp, sm: colProp, md: colProp, lg: colProp, xl: colProp, xxl: colProp, tag: { type: String, default: 'div' } },
  render () {
    const classes = []
    const suffixes = { col: '', xs: '-xs', sm: '-sm', md: '-md', lg: '-lg', xl: '-xl', xxl: '-xxl' }
    Object.keys(suffixes).forEach((key) => {
      const prop = this[key]
      if (prop === true || prop === '') {
        // Vue 2 treated an empty boolean attribute (e.g. <CCol lg>) as true
        if (prop === true || prop === '') classes.push(`col${suffixes[key]}`)
      } else if (typeof prop === 'number' || typeof prop === 'string') {
        classes.push(`col${suffixes[key]}-${prop}`)
      } else if (prop && typeof prop === 'object') {
        if (prop.size) classes.push(`col${suffixes[key]}-${prop.size}`)
        if (prop.offset) classes.push(`offset${suffixes[key]}-${prop.offset}`)
        if (prop.order) classes.push(`order${suffixes[key]}-${prop.order}`)
      }
    })
    return h(this.tag, { class: classes.length ? classes : 'col' }, slot(this.$slots))
  }
}

const sharedCardProps = () => ({
  align: String,
  color: String,
  borderColor: String,
  textColor: String,
  tag: String
})
function cardPart (name, staticClass, defaultTag) {
  return {
    name,
    props: sharedCardProps(),
    render () {
      return h(this.tag || defaultTag, {
        class: [staticClass, {
          [`text-${this.align}`]: this.align,
          [`bg-${this.color}`]: this.color,
          [`border-${this.borderColor}`]: this.borderColor,
          [`text-${this.textColor}`]: this.textColor
        }]
      }, slot(this.$slots))
    }
  }
}
export const CCardBody = cardPart('CCardBody', 'card-body', 'div')
export const CCardHeader = cardPart('CCardHeader', 'card-header', 'header')
export const CCardFooter = cardPart('CCardFooter', 'card-footer', 'footer')
export const CCardTitle = { name: 'CCardTitle', props: { tag: { type: String, default: 'h4' } }, render () { return h(this.tag, { class: 'card-title' }, slot(this.$slots)) } }
export const CCardText = { name: 'CCardText', props: { tag: { type: String, default: 'p' } }, render () { return h(this.tag, { class: 'card-text' }, slot(this.$slots)) } }
export const CCard = {
  name: 'CCard',
  props: { ...sharedCardProps(), bodyWrapper: Boolean, accentColor: String },
  render () {
    const content = this.bodyWrapper ? h(CCardBody, null, { default: () => slot(this.$slots) }) : slot(this.$slots)
    return h(this.tag || 'div', {
      class: ['card', {
        [`card-accent-${this.accentColor}`]: this.accentColor,
        [`text-${this.align}`]: this.align,
        [`bg-${this.color}`]: this.color,
        [`border-${this.borderColor}`]: this.borderColor,
        [`text-${this.textColor}`]: this.textColor
      }]
    }, content)
  }
}
export const CCardGroup = {
  name: 'CCardGroup',
  props: { tag: { type: String, default: 'div' }, deck: Boolean, columns: Boolean },
  render () {
    const type = this.columns ? 'columns' : this.deck ? 'deck' : 'group'
    return h(this.tag, { class: `card-${type}` }, slot(this.$slots))
  }
}

export const CButton = {
  name: 'CButton',
  props: {
    ...linkPropsFactory(),
    block: Boolean,
    shape: String,
    variant: String,
    size: String,
    color: String,
    type: { type: String, default: 'button' },
    pressed: { type: Boolean, default: null }
  },
  emits: ['update:pressed'],
  render () {
    const toggle = this.pressed !== null
    const isLink = Boolean(this.href || this.to)
    const outline = this.variant === 'outline' ? 'outline-' : ''
    const data = {
      class: ['btn', {
        [`btn-${outline}${this.color}`]: this.color,
        [`btn-${this.size}`]: this.size,
        [`btn-ghost-${this.color}`]: this.variant === 'ghost',
        'btn-block': this.block,
        'btn-pill': this.shape === 'pill',
        'btn-square': this.shape === 'square',
        disabled: this.disabled,
        active: this.pressed
      }],
      type: isLink ? undefined : this.type,
      disabled: isLink ? undefined : this.disabled,
      'aria-pressed': toggle ? String(this.pressed) : undefined,
      autocomplete: toggle ? 'off' : undefined,
      // attrs' own onClick falls through and is merged by Vue
      onClick: toggle ? () => this.$emit('update:pressed', !this.pressed) : undefined
    }
    if (isLink) Object.assign(data, pickByKeys(this.$props, Object.keys(linkPropsFactory())))
    return h(isLink ? CLink : 'button', data, isLink ? { default: () => slot(this.$slots) } : slot(this.$slots))
  }
}

export const CButtonClose = {
  name: 'CButtonClose',
  props: { buttonClasses: { type: [String, Array, Object], default: 'close' } },
  render () {
    const content = slot(this.$slots)
    return h('button', { class: this.buttonClasses, type: 'button', 'aria-label': 'Close', innerHTML: content ? undefined : '&times;' }, content)
  }
}

export const CBadge = {
  name: 'CBadge',
  props: { ...linkProps, tag: { type: String, default: 'span' }, color: String, shape: String },
  render () {
    const isLink = this.href || this.to
    const data = { class: ['badge', { [`badge-${this.color}`]: this.color, 'badge-pill': this.shape === 'pill' }] }
    if (isLink) {
      Object.assign(data, pickByKeys(this.$props, Object.keys(linkProps)))
      return h(CLink, data, { default: () => slot(this.$slots) })
    }
    return h(this.tag, data, slot(this.$slots))
  }
}

export const CSpinner = {
  name: 'CSpinner',
  props: { grow: Boolean, color: String, size: String, tag: { type: String, default: 'div' } },
  render () {
    const type = this.grow ? 'grow' : 'border'
    return h(this.tag, {
      role: 'status',
      'aria-hidden': 'false',
      'aria-label': 'Loading',
      class: [`spinner-${type}`, { [`spinner-${type}-${this.size}`]: this.size, [`text-${this.color}`]: this.color }]
    })
  }
}

export const CForm = {
  name: 'CForm',
  props: { inline: Boolean, wasValidated: Boolean },
  render () {
    return h('form', { class: { 'form-inline': this.inline, 'was-validated': this.wasValidated } }, slot(this.$slots))
  }
}

export const CListGroup = {
  name: 'CListGroup',
  props: { tag: { type: String, default: 'ul' }, flush: Boolean, horizontal: [Boolean, String], accent: Boolean },
  render () {
    const hor = this.horizontal
    const suffix = typeof hor === 'string' && hor ? `-${hor}` : ''
    return h(this.tag, {
      role: this.$attrs.role || 'list-items',
      class: ['list-group', {
        'list-group-flush': !hor && this.flush,
        [`list-group-horizontal${suffix}`]: hor || hor === '',
        'list-group-accent': this.accent
      }]
    }, slot(this.$slots))
  }
}

export const CListGroupItem = {
  name: 'CListGroupItem',
  props: { tag: { type: String, default: 'li' }, action: Boolean, color: String, accent: String, ...linkPropsFactory() },
  render () {
    const isLink = this.href || this.to
    const tag = isLink ? CLink : this.tag
    const isAction = Boolean(this.action || this.href || this.to || this.tag === 'button')
    const data = {
      class: ['list-group-item', {
        [`list-group-item-${this.color}`]: this.color,
        [`list-group-item-accent-${this.accent}`]: this.accent,
        'list-group-item-action': isAction,
        active: this.active,
        disabled: this.disabled
      }]
    }
    if (tag === 'button') {
      if (!this.$attrs.type) data.type = 'button'
      if (this.disabled) data.disabled = true
    } else if (isLink) {
      Object.assign(data, pickByKeys(this.$props, Object.keys(linkPropsFactory())))
    }
    return h(tag, data, isLink ? { default: () => slot(this.$slots) } : slot(this.$slots))
  }
}

export const CDropdownHeader = {
  name: 'CDropdownHeader',
  props: { tag: { type: String, default: 'div' }, color: String },
  render () { return h(this.tag, { class: ['dropdown-header', { [`bg-${this.color}`]: this.color }] }, slot(this.$slots)) }
}
export const CDropdownDivider = {
  name: 'CDropdownDivider',
  props: { tag: { type: String, default: 'div' } },
  render () { return h(this.tag, { class: 'dropdown-divider', role: 'separator' }) }
}

/* layout */
function simpleTag (name, defaultTag, classFn, extraProps = {}) {
  return {
    name,
    props: { tag: { type: String, default: defaultTag }, ...extraProps },
    render () { return h(this.tag, { class: classFn(this) }, slot(this.$slots)) }
  }
}
export const CWrapper = simpleTag('CWrapper', 'div', (v) => ['c-wrapper', { 'c-wrapper-fluid': v.fluid }], { fluid: Boolean })
export const CHeader = simpleTag('CHeader', 'header', (v) => ['c-header', `c-header-${v.colorScheme}`, { 'c-header-fixed': v.fixed, 'c-header-with-subheader': v.withSubheader }], {
  colorScheme: { type: String, default: 'light' }, fixed: { type: Boolean, default: true }, withSubheader: Boolean
})
export const CFooter = simpleTag('CFooter', 'footer', (v) => ['c-footer', { 'c-footer-fixed': v.fixed }], { fixed: { type: Boolean, default: true } })
export const CHeaderNav = { name: 'CHeaderNav', render () { return h('ul', { class: 'c-header-nav' }, slot(this.$slots)) } }
export const CHeaderNavItem = { name: 'CHeaderNavItem', render () { return h('li', { class: 'c-header-nav-item' }, slot(this.$slots)) } }
export const CHeaderNavLink = {
  name: 'CHeaderNavLink',
  props: linkPropsFactory(),
  render () { return h(CLink, { class: 'c-header-nav-link', ...this.$props }, { default: () => slot(this.$slots) }) }
}
export const CHeaderBrand = {
  name: 'CHeaderBrand',
  props: { ...linkPropsFactory(), tag: { type: String, default: 'div' } },
  render () {
    const isLink = this.href || this.to
    if (isLink) return h(CLink, { class: 'c-header-brand', ...pickByKeys(this.$props, Object.keys(linkPropsFactory())) }, { default: () => slot(this.$slots) })
    return h(this.tag, { class: 'c-header-brand' }, slot(this.$slots))
  }
}
export const CSidebarNavTitle = { name: 'CSidebarNavTitle', render () { return h('li', { class: 'c-sidebar-nav-title' }, slot(this.$slots)) } }
